// Indents email HTML for editing. n8n writes its drafts as a single line,
// which is unreadable and near-impossible to edit safely.
//
// Deliberately not a real HTML parser and not prettier (a ~1MB browser
// bundle for one button). The rule that makes it safe: every token is
// emitted byte-for-byte as it was found, and only the whitespace *between*
// tokens changes. Nothing is rewritten, reordered or dropped, so the worst
// case is odd-looking indentation rather than a broken email.
//
// Email HTML depends on inline styles and table markup surviving intact, and
// whitespace between inline elements is rendered - so this is offered as an
// explicit button, never applied automatically on load.

// Tags that never have a closing partner, so they must not open a level.
const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr',
]);

// A comment (including an Outlook conditional, which is matched whole and so
// survives untouched), a tag, or a run of text.
const TOKEN = /<!--[\s\S]*?-->|<[^>]+>|[^<]+/g;

export function formatHtml(source: string): string {
  const tokens = source.match(TOKEN);
  if (!tokens) return source;

  const lines: string[] = [];
  let depth = 0;
  const indent = () => '  '.repeat(depth);

  for (const token of tokens) {
    const trimmed = token.trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('<!--')) {
      lines.push(indent() + trimmed);
      continue;
    }

    if (trimmed.startsWith('</')) {
      depth = Math.max(0, depth - 1);
      lines.push(indent() + trimmed);
      continue;
    }

    if (trimmed.startsWith('<')) {
      lines.push(indent() + trimmed);
      const name = /^<\s*([a-zA-Z][a-zA-Z0-9-]*)/.exec(trimmed)?.[1]?.toLowerCase();
      const opensLevel = Boolean(name) && !VOID_TAGS.has(name!) && !trimmed.endsWith('/>');
      if (opensLevel) depth += 1;
      continue;
    }

    // A text node: collapsed to single spaces, which is how HTML renders it
    // anyway. Merge tags like {{contact.PROMO_CODE}} pass through unharmed.
    lines.push(indent() + trimmed.replace(/\s+/g, ' '));
  }

  return lines.join('\n');
}
